import { z } from 'zod';
import { Basis, FamilyKey, GateLabel, PickKind } from './domain.js';
import { CentsSchema } from './money.js';
/**
 * What the engine returns. This is the whole contract between the API and the
 * UI: the browser renders this and computes nothing, which is what keeps the
 * Microsoft-confidential ruleset off the client.
 */
/** One gate on an engagement row, pass or fail, with the reason either way. */
export const Gate = z
    .object({
    label: GateLabel,
    ok: z.boolean(),
    detail: z.string(),
})
    .strict();
/** The band a deal landed in. Null when the deal is below the smallest floor. */
export const SelectedTier = z
    .object({
    size: z.string(),
    floor: z.number().int(),
    ceiling: z.number().int().nullable(),
    minHours: z.number().int().nullable(),
})
    .strict();
/** A band as shown in the expanded detail table, with the match flagged. */
export const BandRow = z
    .object({
    size: z.string(),
    floor: z.number().int(),
    ceiling: z.number().int().nullable(),
    marketA: CentsSchema,
    marketB: CentsSchema,
    marketC: CentsSchema.nullable(),
    isHit: z.boolean(),
})
    .strict();
export const EngagementResult = z
    .object({
    key: z.string(),
    name: z.string(),
    phase: z.string(),
    basis: Basis,
    eligible: z.boolean(),
    gates: z.array(Gate),
    tier: SelectedTier.nullable(),
    bands: z.array(BandRow),
    /** Payout in USD cents for the selected market. Null when there is no band. */
    payoutUsd: CentsSchema.nullable(),
    /**
     * Conditional second payment. Shown separately and deliberately excluded from
     * every total — it pays only if annualised ACR reaches the nominated floor
     * inside the three-month window.
     */
    stageTwoUsd: CentsSchema.nullable(),
    /** Recommended or alternative within an exclusivity group; null means it stacks. */
    pick: PickKind.nullable(),
    /**
     * False when none of the engagement's families carry spend. These rows are
     * shown apart from genuinely blocked ones — they are not relevant to this
     * deal rather than failing a gate on it.
     */
    relevant: z.boolean(),
    /** The value this engagement was sized on, in its own basis units. */
    sizedOn: z.number().int().nullable(),
    /** Contract value attributable to this engagement, USD cents. */
    ownContractValueUsd: CentsSchema.nullable(),
    /** Payout as a percentage of that contract value. Null when unknowable. */
    yieldPct: z.number().nullable(),
    /** Payout per guide minimum hour, USD cents. */
    perHourUsd: CentsSchema.nullable(),
    /** Why the capability gate failed, for the "one capability away" list. */
    capabilityDetail: z.array(z.string()),
    workloads: z.string(),
    duration: z.string().nullable(),
    sizingLabel: z.string().nullable(),
    poe: z.array(z.string()),
    notes: z.array(z.string()),
})
    .strict();
/**
 * Aggregated workload figures, in the **entry currency** the user typed — that is
 * what the workload table displays back to them. Only the engine's internal
 * sizing figures are converted to USD.
 */
export const WorkloadTotals = z
    .object({
    incrementalYearly: CentsSchema,
    incrementalMonthly: CentsSchema,
    baselineYearly: CentsSchema,
    baselineMonthly: CentsSchema,
    totalYearly: CentsSchema,
    byCategory: z.record(z.string(), CentsSchema),
    byFamily: z.record(FamilyKey, CentsSchema),
    /** Rows with no family — networking, backup and DR, other. */
    supporting: CentsSchema,
    /** Sum of `byFamily`; `incrementalYearly === familyTotal + supporting`. */
    familyTotal: CentsSchema,
    subFloors: z.object({ db: CentsSchema, agentic: CentsSchema, mdc: CentsSchema }).strict(),
    defenderOnly: CentsSchema,
    sentinelOnly: CentsSchema,
})
    .strict();
/** One line of the deal-value strip. */
export const ContractValueLine = z
    .object({
    basis: z.enum(['acr', 'acr_total', 'acv', 'seats', 'mpu']),
    label: z.string(),
    usd: CentsSchema.nullable(),
    /** False when the figure cannot be computed — e.g. no seat price entered. */
    known: z.boolean(),
    note: z.string().nullable(),
})
    .strict();
/**
 * Something the user should know about how the numbers were produced.
 *
 * The legacy tool had one genuinely dangerous silence: entering amounts in AUD
 * without setting an FX rate made it fall back to a rate of 1, so AUD figures
 * were measured against USD bands. The arithmetic is kept identical for parity,
 * but it is now stated rather than hidden.
 */
export const ResultWarning = z
    .object({
    kind: z.enum(['fx_unset', 'single_figure_mode', 'no_input']),
    message: z.string(),
})
    .strict();
/** An advisory flag on the workload mix. Guidance, never a gate. */
export const WorkloadFlag = z
    .object({
    kind: z.enum(['agentic_share', 'db_share', 'mdc_shortfall', 'baseline_decommission']),
    message: z.string(),
})
    .strict();
export const ScenarioResult = z
    .object({
    engineVersion: z.string(),
    guideVersion: z.string(),
    rulesetHash: z.string(),
    rows: z.array(EngagementResult),
    /** Net new consumption the bands measure, USD cents. */
    incrementalAcrUsd: CentsSchema,
    /** Including the existing environment. */
    totalAcrUsd: CentsSchema,
    workloadTotals: WorkloadTotals.nullable(),
    workloadFlags: z.array(WorkloadFlag),
    warnings: z.array(ResultWarning),
    contractValueLines: z.array(ContractValueLine),
    /** Genuine contract-value bases in play. One means a single ratio is meaningful. */
    valueBasisCount: z.number().int().min(0),
    /**
     * The real number: one recommendation per exclusivity group plus everything
     * that stacks. Excludes alternatives, which are mutually exclusive with the
     * recommendation and would double-count.
     */
    claimableTotalUsd: CentsSchema,
    /** Every eligible row summed, including alternatives. Over-counts by design. */
    eligibleTotalUsd: CentsSchema,
    eligibleCount: z.number().int().min(0),
    /** Why the recommended post-sales engagement won. */
    recommendationReasons: z.array(z.string()),
    /** Relevant post-sales rows that would pay more but are blocked. */
    higherBlockedKeys: z.array(z.string()),
    /** Blocked rows whose ONLY failing gate is partner capability. */
    oneCapabilityAwayKeys: z.array(z.string()),
})
    .strict();
//# sourceMappingURL=result.js.map